import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GripVertical, Minimize2, Maximize2 } from 'lucide-react';

/**
 * DraggablePanel - Macht jedes Panel verschiebbar
 *
 * Props:
 * - id: Eindeutige ID für localStorage
 * - title: Panel-Titel in der Titelleiste
 * - defaultPosition: { x, y } Startposition
 * - defaultMinimized: boolean
 * - children: Panel-Inhalt
 * - className: Zusätzliche CSS-Klassen
 * - onClose: Optional, schließt Panel
 */
export function DraggablePanel({
  id,
  title,
  defaultPosition = { x: 16, y: 16 },
  defaultMinimized = false,
  children,
  className = '',
  onClose,
  width = 'w-72',
  zIndex = 10,
}) {
  // Position aus localStorage laden oder Default verwenden
  const loadPosition = () => {
    try {
      const saved = localStorage.getItem(`panel-pos-${id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { x: parsed.x ?? defaultPosition.x, y: parsed.y ?? defaultPosition.y };
      }
    } catch (e) {}
    return defaultPosition;
  };

  const loadMinimized = () => {
    try {
      const saved = localStorage.getItem(`panel-min-${id}`);
      if (saved !== null) return saved === 'true';
    } catch (e) {}
    return defaultMinimized;
  };

  const [position, setPosition] = useState(loadPosition);
  const [isMinimized, setIsMinimized] = useState(loadMinimized);
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // Position speichern
  useEffect(() => {
    localStorage.setItem(`panel-pos-${id}`, JSON.stringify(position));
  }, [id, position]);

  // Minimized-State speichern
  useEffect(() => {
    localStorage.setItem(`panel-min-${id}`, String(isMinimized));
  }, [id, isMinimized]);

  // Drag-Handler
  const handleMouseDown = useCallback((e) => {
    // Nur auf Titelleiste reagieren
    if (e.target.closest('.panel-content')) return;

    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: position.x,
      posY: position.y,
    };

    const handleMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - dragStartRef.current.x;
      const dy = moveEvent.clientY - dragStartRef.current.y;

      // Neue Position berechnen
      let newX = dragStartRef.current.posX + dx;
      let newY = dragStartRef.current.posY + dy;

      // Grenzen prüfen (Panel im Viewport halten)
      const panel = panelRef.current;
      if (panel) {
        const rect = panel.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - 40; // Mindestens Titelleiste sichtbar

        newX = Math.max(0, Math.min(newX, maxX));
        newY = Math.max(0, Math.min(newY, maxY));
      }

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [position]);

  // Minimieren togglen
  const toggleMinimize = useCallback((e) => {
    e.stopPropagation();
    setIsMinimized(prev => !prev);
  }, []);

  // Verhindere Event-Bubbling nur wenn auf Panel geklickt wird
  const handlePanelMouseDown = useCallback((e) => {
    // Nur stoppen wenn direkt auf Panel-Element geklickt (nicht auf Canvas dahinter)
    e.stopPropagation();
  }, []);

  return (
    <div
      ref={panelRef}
      className={`absolute bg-white rounded-lg shadow-lg overflow-hidden flex flex-col ${width} ${className}`}
      style={{
        left: position.x,
        top: position.y,
        zIndex: isDragging ? 1000 : zIndex,
        maxHeight: isMinimized ? 'auto' : 'calc(100vh - 8rem)',
        pointerEvents: 'auto', // Panel fängt Events
      }}
      onMouseDown={handlePanelMouseDown}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Titelleiste - Drag Handle */}
      <div
        className={`flex items-center justify-between px-2 py-1.5 bg-gray-100 border-b cursor-move select-none ${
          isDragging ? 'bg-blue-100' : 'hover:bg-gray-200'
        }`}
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-1">
          <GripVertical size={14} className="text-gray-400" />
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleMinimize}
            className="p-1 hover:bg-gray-300 rounded"
            title={isMinimized ? 'Maximieren' : 'Minimieren'}
          >
            {isMinimized ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
          </button>
          {onClose && (
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="p-1 hover:bg-red-100 hover:text-red-600 rounded"
              title="Schließen"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Inhalt */}
      {!isMinimized && (
        <div className="panel-content flex-1 overflow-y-auto">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Einfacher Hook um Panel-Sichtbarkeit zu verwalten
 */
export function usePanelVisibility(defaultVisible = {}) {
  const [visibility, setVisibility] = useState(() => {
    try {
      const saved = localStorage.getItem('panel-visibility');
      if (saved) return { ...defaultVisible, ...JSON.parse(saved) };
    } catch (e) {}
    return defaultVisible;
  });

  const togglePanel = useCallback((panelId) => {
    setVisibility(prev => {
      const next = { ...prev, [panelId]: !prev[panelId] };
      localStorage.setItem('panel-visibility', JSON.stringify(next));
      return next;
    });
  }, []);

  const showPanel = useCallback((panelId) => {
    setVisibility(prev => {
      const next = { ...prev, [panelId]: true };
      localStorage.setItem('panel-visibility', JSON.stringify(next));
      return next;
    });
  }, []);

  const hidePanel = useCallback((panelId) => {
    setVisibility(prev => {
      const next = { ...prev, [panelId]: false };
      localStorage.setItem('panel-visibility', JSON.stringify(next));
      return next;
    });
  }, []);

  return { visibility, togglePanel, showPanel, hidePanel };
}
